import { inject, Injectable } from '@angular/core';
import { TaskCtBatch } from '@ps-gui/claims-terminal-shared';
import { from, Observable, switchMap } from 'rxjs';
import { PersonenschadenApiService } from '../api/personenschaden-api.service';
import { TaskCtBatchCompletionDto, TaskDocumentEntry } from '../../models/interfaces';
import { PersistDataService } from '../app-state/persist-data.service';
import { ExtractedDataStoreService } from '../extracted-data/extracted-data-store.service';
import { CtBatchExtractionApiService } from '../api/ct-batch-extraction-api.service';
import { CtBatchRedirectStoreService } from './ct-batch-redirect-store.service';
import { associateBy, Navigate } from '@ps-gui/shared-utils';
import { BehandlerAssignmentService } from '@ps-gui/shared-document-preview';
import { mapToFragebogenData } from './task-fragebogen-mapper';

@Injectable({ providedIn: 'root' })
export class TaskCtBatchTransmissionService {
  private readonly personenschadenApiService = inject(PersonenschadenApiService);
  private readonly persistDataService = inject(PersistDataService);
  private readonly behandlerAssignmentService = inject(BehandlerAssignmentService);
  private readonly extractedDataStoreService = inject(ExtractedDataStoreService);
  private readonly ctBatchExtractionApiService = inject(CtBatchExtractionApiService);
  private readonly redirectStore = inject(CtBatchRedirectStoreService);

  transmitAndRedirect(ctBatch: TaskCtBatch): Observable<unknown> {
    const behandlers = associateBy(this.persistDataService.behandlerData, (b) => b.index ?? 0);

    const documentData = ctBatch.documents
      .filter((d) => d.documentClasses.includes('25_Q3_EUCON_FRAGEBOGEN'))
      .map((document) => {
        const documentId = document.id;

        const extractedFragebogenData = mapToFragebogenData(
          this.extractedDataStoreService.getExtractedDocumentDataSnapshot(documentId),
        );

        const documentData: TaskDocumentEntry = {
          documentId,
          extractedFragebogenData,
        };

        const assignedBehandlerIndex =
          this.behandlerAssignmentService.getBehandlerAssignmentData(documentId)?.behandlerIndex;

        if (assignedBehandlerIndex !== undefined) {
          documentData.assignedBehandler = behandlers[assignedBehandlerIndex];
        }

        return documentData;
      });

    const request: TaskCtBatchCompletionDto = {
      taskId: ctBatch.taskId,
      documentData,
    };

    return this.personenschadenApiService.completeTaskCtBatch(request).pipe(
      switchMap(() => this.ctBatchExtractionApiService.signalCtBatchCt2Completion(ctBatch.id)),
      switchMap(() => from(this.redirectAfterOrder(ctBatch))),
    );
  }

  private async redirectAfterOrder(ctBatch: TaskCtBatch): Promise<void> {
    this.redirectStore.setDigideskRedirectUrl(ctBatch.id, ctBatch.taskId);
    await this.persistDataService.reset();
    Navigate.replace(this.redirectStore.getRedirectUrl(ctBatch));
  }
}
